import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Breadcrumbs, 
  Link, 
  Divider, 
  Paper,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Security, 
  Info, 
  Gavel, 
  Person, 
  Storage, 
  VerifiedUser, 
  Public, 
  Timer, 
  ExitToApp 
} from '@mui/icons-material';

const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();

  const sections = [
    {
      id: 'information-collected',
      title: 'Information We Collect',
      icon: Info,
      content: `We collect personal information that you voluntarily provide to us when you express interest in obtaining information about us or our services, when you participate in activities on our website, or otherwise when you contact us. The personal information we collect may include: name, email address, phone number, and other information you choose to provide.`
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      icon: Storage,
      content: `We use personal information collected via our website for a variety of business purposes including: to provide and maintain our services, to process your requests, to contact you regarding your inquiry, to send you marketing and promotional communications, and to fulfill and manage your orders.`
    },
    {
      id: 'information-sharing',
      title: 'Will Your Information Be Shared With Anyone?',
      icon: Public,
      content: `We only share your personal information with third parties when we have your consent, to comply with laws, to provide you with services, to protect your rights, or to fulfill business obligations. We may process or share your data that we hold based on the following legal basis: consent, legitimate interests, performance of a contract, and legal obligations.`
    },
    {
      id: 'cookies',
      title: 'Do We Use Cookies and Other Tracking Technologies?',
      icon: Timer,
      content: `We may use cookies and similar tracking technologies to access or store information. However, you may refuse to accept browser cookies by activating the appropriate setting on your browser. However, if you select this setting you may be unable to access certain parts of our website.`
    },
    {
      id: 'data-security',
      title: 'How Do We Keep Your Information Safe?',
      icon: Security,
      content: `We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards, no security system is impenetrable, and we cannot guarantee the security of our systems 100%.`
    },
    {
      id: 'data-retention',
      title: 'How Long Do We Keep Your Information?',
      icon: Timer,
      content: `We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy policy, unless a longer retention period is required or permitted by law.`
    },
    {
      id: 'privacy-rights',
      title: 'What Are Your Privacy Rights?',
      icon: Gavel,
      content: `In some regions, such as the European Economic Area, you have rights that allow you greater access to and control over your personal information. You may review, change, or terminate your account at any time. In some regions, you also have the right to: access your data, request data rectification, withdraw consent, data erasure, restriction of processing, object to processing, and data portability.`
    },
    {
      id: 'controls-for-dnd',
      title: 'Controls for Do-Not-Track Features',
      icon: VerifiedUser,
      content: `Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized.`
    },
    {
      id: 'policy-updates',
      title: 'Do We Make Updates to This Policy?',
      icon: ExitToApp,
      content: `We may update this privacy policy from time to time. The updated version will be indicated by an updated "Revised" date and the updated version will be effective as soon as it is accessible. We encourage you to review this privacy policy frequently to be informed of how we are protecting your information.`
    }
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'background.default' }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link component={RouterLink} to="/" color="inherit">
            Home
          </Link>
          <Typography color="text.primary">Privacy Policy</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h1" 
            component="h1" 
            sx={{ 
              fontSize: { xs: '2.5rem', md: '3.5rem' }, 
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Privacy Policy
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto',
              mb: 2 
            }}
          >
            Last Updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
          <Divider sx={{ width: '60px', mx: 'auto', borderWidth: 2, borderColor: theme.palette.primary.main, my: 3 }} />
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto' 
            }}
          >
            This Privacy Policy describes how Chishty UK Immigration Law & Visa Advisor ("we," "us," or "our") collects, uses, and shares your personal information when you visit our website, use our services, or otherwise interact with us.
          </Typography>
        </Box>

        {/* Table of Contents */}
        <Paper elevation={0} sx={{ p: 4, mb: 6, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
            Table of Contents
          </Typography>
          <List sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
            {sections.map((section) => (
              <ListItem 
                key={section.id} 
                component="a" 
                href={`#${section.id}`} 
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.03)'
                  }
                }}
              >
                <ListItemIcon>
                  <section.icon color="primary" />
                </ListItemIcon>
                <ListItemText primary={section.title} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Privacy Policy Content */}
        <Box sx={{ mb: 6 }}>
          {sections.map((section) => (
            <Box key={section.id} id={section.id} sx={{ mb: 6, scrollMarginTop: '80px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                <section.icon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ ml: { xs: 0, sm: 6 }, pl: { xs: 0, sm: 1 } }}>
                {section.content}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Contact Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            borderRadius: 2, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.primary.light}20)`,
            border: `1px solid ${theme.palette.primary.main}20`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Person sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Contact Us
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            If you have questions or comments about this privacy policy, you may email us at contact@chishtyukimmigrationlaw.info or by post to:
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
            Chishty UK Immigration Law & Visa Advisor<br />
            1 Turner Road Atif Centre<br />
            High Court Lahore<br />
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy; 